import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { PublicLayout } from "@/components/PublicLayout";
import api from "@/lib/api";

const toYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

export default function Book() {
    const [params] = useSearchParams();
    const preselect = params.get("service");
    const [settings, setSettings] = useState(null);
    const [services, setServices] = useState([]);
    const [availability, setAvailability] = useState(null);
    const [payouts, setPayouts] = useState([]);
    const [date, setDate] = useState(null);
    const [slots, setSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [serviceId, setServiceId] = useState("");
    const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
    const [mode, setMode] = useState("in_person");
    const [promoCode, setPromoCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [confirmed, setConfirmed] = useState(null);
    const [paymentEnabled, setPaymentEnabled] = useState(false);

    useEffect(() => {
        api.get("/settings").then((r) => setSettings(r.data));
        api.get("/services").then((r) => {
            setServices(r.data);
            if (preselect && r.data.find((s) => s.id === preselect)) setServiceId(preselect);
            else if (r.data.length && !serviceId) setServiceId(r.data[0].id);
        });
        api.get("/availability").then((r) => setAvailability(r.data));
        api.get("/payouts").then((r) => setPayouts(r.data));
        api.get("/payments/config").then((r) => setPaymentEnabled(r.data.enabled));
    }, []);

    useEffect(() => {
        if (!date) { setSlots([]); return; }
        api.get("/availability/slots", { params: { date_str: toYMD(date) } })
            .then((r) => setSlots(r.data.slots || []));
        setSelectedSlot(null);
    }, [date]);

    const blockedSet = useMemo(() => new Set(availability?.blocked_dates || []), [availability]);
    const weekly = availability?.weekly || {};
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const isDisabled = (d) => {
        if (d < new Date(new Date().setHours(0, 0, 0, 0))) return true;
        if (blockedSet.has(toYMD(d))) return true;
        const name = dayNames[d.getDay()];
        const windows = weekly[name] || [];
        return windows.length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!date || !selectedSlot || !serviceId) {
            toast.error("Pick a date, time, and service");
            return;
        }
        if (!form.name || !form.email) {
            toast.error("Name and email are required");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/appointments", {
                ...form,
                service_id: serviceId,
                date: toYMD(date),
                time: selectedSlot,
                mode,
                promo_code: promoCode.trim(),
            });
            // If it's a paid booking (no promo) AND Stripe is enabled, go straight to Stripe Checkout
            const needsPayment = !data.promo_applied && data.price_paid > 0;
            if (needsPayment && paymentEnabled) {
                try {
                    const { data: co } = await api.post("/payments/checkout", {
                        appointment_id: data.id,
                        origin_url: window.location.origin,
                    });
                    window.location.href = co.checkout_url;
                    return;
                } catch (payErr) {
                    // fall back to manual payout instructions
                    toast.error("Card payment unavailable — showing manual instructions instead");
                }
            }
            toast.success("Appointment request sent!");
            setConfirmed(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not book — try another time");
        } finally {
            setSubmitting(false);
        }
    };

    if (confirmed) {
        const needsPayment = !confirmed.promo_applied && confirmed.price_paid > 0;
        return (
            <PublicLayout settings={settings}>
                <section className="max-w-2xl mx-auto px-6 py-16" data-testid="booking-confirmation">
                    {needsPayment ? (
                        <>
                            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[hsl(var(--accent))]">Payment required</div>
                            <h1 className="font-display text-4xl sm:text-5xl mt-3">Almost there, {confirmed.name}.</h1>
                            <p className="mt-4 text-[hsl(var(--muted-foreground))]">
                                Your seat for <strong>{confirmed.service_title}</strong> on <strong>{confirmed.date}</strong> at <strong>{confirmed.time}</strong> is on hold. Complete payment below to confirm.
                            </p>

                            <div className="mt-8 card-clay p-6 border-l-4 border-[hsl(var(--accent))]" data-testid="payout-methods">
                                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Amount due</div>
                                        <div className="font-display text-3xl mt-1 text-[hsl(var(--primary))]">${confirmed.price_paid}</div>
                                    </div>
                                    <span className="chip">Awaiting payment</span>
                                </div>
                                <div className="divider-gold mt-4"></div>
                                <h3 className="font-display text-lg mt-4">Choose how you'd like to pay</h3>
                                {payouts.length > 0 ? (
                                    <ul className="mt-4 space-y-3">
                                        {payouts.map((p) => (
                                            <li key={p.id} className="border border-[hsl(var(--border))] rounded-md p-4" data-testid={`payout-${p.id}`}>
                                                <div className="text-sm font-medium">{p.label}</div>
                                                {p.details && <div className="mt-1 font-mono text-sm text-[hsl(var(--primary))] break-all">{p.details}</div>}
                                                {p.instructions && <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">{p.instructions}</div>}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-3">Payment instructions will be emailed to you at {confirmed.email} shortly.</p>
                                )}
                                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-5">
                                    A confirmation has been sent to <strong>{confirmed.email}</strong>. Once payment arrives, your appointment moves to confirmed and (if online) your meeting link becomes active.
                                </p>
                            </div>

                            {confirmed.meeting_url && (
                                <div className="mt-6 card-clay p-6" data-testid="meeting-link-block">
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Your meeting link (available after payment)</div>
                                    <a href={confirmed.meeting_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block break-all font-mono text-sm text-[hsl(var(--primary))] underline" data-testid="meeting-link">
                                        {confirmed.meeting_url}
                                    </a>
                                </div>
                            )}

                            <a href="/" className="pill btn-primary inline-block px-6 py-3 mt-10 text-sm font-medium">Back to home</a>
                        </>
                    ) : (
                        <div className="text-center">
                            <div className="font-mono text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Confirmed</div>
                            <h1 className="font-display text-4xl sm:text-5xl mt-3">Thank you, {confirmed.name}.</h1>
                            <p className="mt-6 text-[hsl(var(--muted-foreground))]">
                                Your request for <strong>{confirmed.service_title}</strong> on{" "}
                                <strong>{confirmed.date}</strong> at <strong>{confirmed.time}</strong>{" "}
                                ({confirmed.mode === "online" ? "Online video call" : "In person"}) has been sent.
                                A confirmation has been emailed to {confirmed.email}.
                            </p>
                            {confirmed.promo_applied && (
                                <div className="mt-6 inline-block px-4 py-2 rounded-md bg-[hsl(var(--accent))] text-white text-sm font-mono uppercase tracking-widest" data-testid="promo-applied-badge">
                                    Promo applied — no charge
                                </div>
                            )}
                            {confirmed.meeting_url && (
                                <div className="mt-8 card-clay p-6 text-left" data-testid="meeting-link-block">
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Your meeting link</div>
                                    <a href={confirmed.meeting_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block break-all font-mono text-sm text-[hsl(var(--primary))] underline" data-testid="meeting-link">
                                        {confirmed.meeting_url}
                                    </a>
                                    <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">Save this link — it's also in the confirmation email. Join at your appointment time.</p>
                                </div>
                            )}
                            <a href="/" className="pill btn-primary inline-block px-6 py-3 mt-10 text-sm font-medium">Back to home</a>
                        </div>
                    )}
                </section>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout settings={settings}>
            <section className="max-w-6xl mx-auto px-6 py-16">
                <div className="font-mono text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Book</div>
                <h1 className="font-display text-4xl sm:text-5xl mt-3">Schedule your appointment.</h1>
                <p className="mt-4 max-w-xl text-[hsl(var(--muted-foreground))]">
                    Pick an available date and time — you'll receive confirmation by email.
                </p>

                <div className="mt-10 grid lg:grid-cols-2 gap-10">
                    <div className="card-clay p-6" data-testid="calendar-panel">
                        <label className="font-mono text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Service</label>
                        <select
                            value={serviceId}
                            onChange={(e) => setServiceId(e.target.value)}
                            className="mt-2 w-full bg-[hsl(var(--input))] border border-[hsl(var(--border))] rounded-md px-3 py-2 text-sm"
                            data-testid="service-select"
                        >
                            {services.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {`${s.title} — ${s.is_paid ? `$${s.price}` : "Free"}`}
                                </option>
                            ))}
                        </select>

                        <div className="mt-6">
                            <label className="font-mono text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Date</label>
                            <div className="mt-2">
                                <Calendar
                                    mode="single"
                                    selected={date}
                                    onSelect={setDate}
                                    disabled={isDisabled}
                                    data-testid="date-calendar"
                                />
                            </div>
                        </div>

                        <div className="mt-6">
                            <label className="font-mono text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Available times</label>
                            {!date && (
                                <div className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Pick a date to see slots.</div>
                            )}
                            {date && slots.length === 0 && (
                                <div className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">No available times on this day.</div>
                            )}
                            <div className="mt-3 flex flex-wrap gap-2" data-testid="slot-grid">
                                {slots.map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => setSelectedSlot(s)}
                                        data-testid={`slot-${s}`}
                                        className={`px-3 py-2 rounded-md text-sm border transition-colors ${
                                            selectedSlot === s
                                                ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                                                : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"
                                        }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <form onSubmit={submit} className="card-clay p-6 flex flex-col gap-4" data-testid="booking-form">
                        <div>
                            <label className="font-mono text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Meeting type</label>
                            <div className="mt-2 grid grid-cols-2 gap-2" data-testid="mode-toggle">
                                <button
                                    type="button"
                                    onClick={() => setMode("in_person")}
                                    data-testid="mode-in-person"
                                    className={`px-3 py-2 rounded-md text-sm border transition-colors ${mode === "in_person" ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
                                >
                                    In person
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMode("online")}
                                    data-testid="mode-online"
                                    className={`px-3 py-2 rounded-md text-sm border transition-colors ${mode === "online" ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
                                >
                                    Online (video call)
                                </button>
                            </div>
                        </div>
                        <div>
                            <label className="font-mono text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Full name</label>
                            <input
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="mt-2 w-full bg-[hsl(var(--input))] border border-[hsl(var(--border))] rounded-md px-3 py-2 text-sm"
                                data-testid="input-name"
                            />
                        </div>
                        <div>
                            <label className="font-mono text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Email</label>
                            <input
                                required
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="mt-2 w-full bg-[hsl(var(--input))] border border-[hsl(var(--border))] rounded-md px-3 py-2 text-sm"
                                data-testid="input-email"
                            />
                        </div>
                        <div>
                            <label className="font-mono text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Phone (optional)</label>
                            <input
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                className="mt-2 w-full bg-[hsl(var(--input))] border border-[hsl(var(--border))] rounded-md px-3 py-2 text-sm"
                                data-testid="input-phone"
                            />
                        </div>
                        <div>
                            <label className="font-mono text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Notes</label>
                            <textarea
                                rows={4}
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                className="mt-2 w-full bg-[hsl(var(--input))] border border-[hsl(var(--border))] rounded-md px-3 py-2 text-sm"
                                data-testid="input-notes"
                            />
                        </div>
                        <div>
                            <label className="font-mono text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Promo code (optional)</label>
                            <input
                                value={promoCode}
                                onChange={(e) => setPromoCode(e.target.value)}
                                placeholder="Have one? Enter it here"
                                className="mt-2 w-full bg-[hsl(var(--input))] border border-[hsl(var(--border))] rounded-md px-3 py-2 text-sm font-mono uppercase tracking-wide"
                                data-testid="input-promo-code"
                                autoComplete="off"
                            />
                        </div>

                        <div className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
                            {date && selectedSlot ? (
                                <>Booking <strong>{toYMD(date)}</strong> at <strong>{selectedSlot}</strong> · <span className="font-mono uppercase text-xs">{mode === "online" ? "Video call" : "In person"}</span></>
                            ) : (
                                "Select a date and time first."
                            )}
                        </div>

                        <button
                            disabled={submitting || !date || !selectedSlot}
                            className="pill btn-primary px-6 py-3 text-sm font-medium mt-2 disabled:opacity-50"
                            data-testid="submit-booking-btn"
                        >
                            {submitting ? "Sending..." : "Request appointment"}
                        </button>
                    </form>
                </div>
            </section>
        </PublicLayout>
    );
}
